// src/components/Accounts/UserDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { AccountCircle, ExitToApp } from '@mui/icons-material';
import { getCurrentUser, getUserProfile, logout } from '../../services/authService';
import { UserInfo } from '../../services/authService';

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // First check if we have user data in localStorage
        const currentUser = getCurrentUser();

        if (!currentUser) {
          // If no user is logged in, redirect to login page
          navigate('/login');
          return;
        }

        // Set initial data from localStorage
        setUser(currentUser);

        // Then fetch latest user profile from API
        const profile = await getUserProfile();
        setUser(prev => ({ ...prev!, ...profile }));
      } catch (err: any) {
        setError('Failed to load user profile. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mr: 2 }}>
            <AccountCircle fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h5">
              Welcome, {user?.username}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {user?.email}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Account Details
        </Typography>

        <List>
          <ListItem>
            <ListItemText primary="Username" secondary={user?.username} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Email" secondary={user?.email} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Role" secondary={user?.role} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Account ID" secondary={user?._id} />
          </ListItem>
        </List>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ExitToApp />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserDashboard;
